/*
  storage.js — the app's single data seam.

  The app calls window.storage.get / .set / .delete / .list and awaits the result.
  Two implementations live here behind the SAME API:

    - makeLocal()    → the browser's localStorage (one device, nothing syncs)
    - makeSupabase() → real per-record tables on Supabase (all devices, backed up)

  Which one runs is decided by VITE_STORAGE_BACKEND / the runtime flag
  (see supabaseClient.js). The app itself never changes.

  SUPABASE MODE — Stage 1.2 (per-record decomposition):
  The app still *thinks* in one big blob (pooldesk:data:v5) plus per-client
  lists (visits/photos/tests/quotes). This adapter translates:

    blob.clients[]        → table `clients`        one ROW PER CUSTOMER
    blob.jobs[]           → table `jobs`           one row per work order
    blob.parts[] etc.     → their own tables       one row per record
    blob.schedule etc.    → table `app_settings`   one row per setting
    pooldesk:visits:<id>  → table `visits`         one row per customer's list
    (same for photos / tests / quotes)

  On save, only records that actually CHANGED are written, and removed records
  are deleted — so two people saving different customers at the same time
  each touch only their own rows. That kills the last-write-wins blob problem.

  First load auto-migrates: if the tables are empty but the old kv_store blob
  exists, it is split into the tables once, then flagged done. The kv rows are
  left in place as a frozen archive.
*/
import { supabase, backendMode } from "./supabaseClient";

const PREFIX = ""; // keys already carry their own "pooldesk:" prefix
const STORE_KEY = "pooldesk:data:v5";

// blob collection -> table (arrays of records with .id)
export const COLLECTION_KEYS = [
  "clients", "jobs", "parts", "services", "terms", "revCentres", "serviceTypes",
  "posSales", "posHeld", "specialOrders", "staff", "punches", "messages",
  "timeOff", "purchaseOrders", "repairs", "followups", "jobTypes", "rentalUnits", "tools", "campaigns",
];
const COLLECTIONS = {
  clients: "clients", jobs: "jobs", parts: "parts", services: "services",
  terms: "terms", revCentres: "rev_centres", serviceTypes: "service_types",
  posSales: "pos_sales", posHeld: "pos_held", specialOrders: "special_orders",
  staff: "staff", punches: "punches", messages: "messages", timeOff: "time_off",
  purchaseOrders: "purchase_orders", repairs: "repairs", followups: "followups", jobTypes: "job_types", rentalUnits: "rental_units", tools: "tools", campaigns: "campaigns",
};
// blob fields that are single values -> app_settings rows (wrapped as {v: ...})
const SINGLETONS = ["recent", "techs", "schedule", "roles", "currentRole", "levels", "numbering", "businessProfile", "assistantSettings", "supplierInfo", "fleetUnits"];
// per-client list keys -> tables (one row per client, data = the whole list)
const PER_CLIENT = [
  ["pooldesk:visits:", "visits"],
  ["pooldesk:photos:", "photos"],
  ["pooldesk:tests:", "water_tests"],
  ["pooldesk:quotes:", "quotes"],
];
const MIGRATED_FLAG = "migrated_v1";
const CHUNK = 200;

function makeLocal() {
  return {
    async get(key) {
      const raw = localStorage.getItem(PREFIX + key);
      if (raw === null) throw new Error("No such key: " + key);
      return { key, value: raw, shared: false };
    },
    async set(key, value) {
      try {
        localStorage.setItem(PREFIX + key, String(value));
      } catch (err) {
        console.error("Storage full while writing", key, err);
        throw err;
      }
      return { key, value, shared: false };
    },
    async delete(key) {
      localStorage.removeItem(PREFIX + key);
      return { key, deleted: true, shared: false };
    },
    async list(prefix = "") {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PREFIX + prefix)) keys.push(k.slice(PREFIX.length));
      }
      return { keys, prefix, shared: false };
    },
  };
}

// Order-insensitive serialization for change detection. Postgres jsonb re-sorts
// object keys, and the app re-orders them again on load — a plain JSON.stringify
// comparison would call every record "changed" and rewrite the world on each save
// (which resurrects the last-write-wins bug). Sorting keys first means only real
// value changes count as changes.
function sortKeys(v) {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === "object") {
    const o = {};
    for (const k of Object.keys(v).sort()) o[k] = sortKeys(v[k]);
    return o;
  }
  return v;
}
const stable = (v) => JSON.stringify(sortKeys(v));

function makeSupabase() {
  // Snapshot of what the server held at last load/save — used to write only diffs.
  let cache = null; // { col: {name: Map(id -> stableJson)}, single: {name: stableJson} }

  const chunked = async (arr, fn) => {
    for (let i = 0; i < arr.length; i += CHUNK) await fn(arr.slice(i, i + CHUNK));
  };
  const bail = (r) => { if (r.error) throw r.error; return r; };

  const perClientTable = (key) => {
    for (const [pfx, table] of PER_CLIENT) {
      if (key.startsWith(pfx)) return { table, clientId: key.slice(pfx.length) };
    }
    return null;
  };

  async function loadBlob() {
    const names = Object.keys(COLLECTIONS);
    const results = await Promise.all([
      ...names.map((n) => supabase.from(COLLECTIONS[n]).select("id,data")),
      supabase.from("app_settings").select("id,data"),
    ]);
    results.forEach(bail);
    const blob = {};
    cache = { col: {}, single: {} };
    names.forEach((n, i) => {
      const rows = results[i].data || [];
      blob[n] = rows.map((r) => r.data);
      cache.col[n] = new Map(rows.map((r) => [String(r.id), stable(r.data)]));
    });
    const settings = results[names.length].data || [];
    let migrated = false;
    for (const row of settings) {
      if (row.id === MIGRATED_FLAG) { migrated = true; continue; }
      if (SINGLETONS.includes(row.id)) {
        blob[row.id] = row.data && row.data.v !== undefined ? row.data.v : row.data;
        cache.single[row.id] = stable(blob[row.id]);
      }
    }
    return { blob, migrated };
  }

  async function saveBlob(blob) {
    if (!cache) cache = { col: {}, single: {} };
    const jobs = [];
    for (const [name, table] of Object.entries(COLLECTIONS)) {
      const arr = Array.isArray(blob[name]) ? blob[name] : [];
      const prev = cache.col[name] || new Map();
      const next = new Map();
      const upserts = [];
      for (const rec of arr) {
        if (!rec || rec.id === undefined || rec.id === null) continue;
        const id = String(rec.id);
        const json = stable(rec);
        next.set(id, json);
        if (prev.get(id) !== json) upserts.push({ id, data: rec, updated_at: new Date().toISOString() });
      }
      const removed = [...prev.keys()].filter((id) => !next.has(id));
      jobs.push((async () => {
        await chunked(upserts, async (rows) => bail(await supabase.from(table).upsert(rows, { onConflict: "id" })));
        // Deletes are best-effort: an RLS-denied cleanup (e.g. old punches trimmed by a
        // non-admin session) must not fail the save that carried real work.
        await chunked(removed, async (ids) => {
          const r = await supabase.from(table).delete().in("id", ids);
          if (r.error) console.warn("Cleanup skipped on", table, r.error.message);
        });
        cache.col[name] = next;
      })());
    }
    const settingUpserts = [];
    for (const name of SINGLETONS) {
      if (blob[name] === undefined) continue;
      const json = stable(blob[name]);
      if (cache.single[name] !== json) {
        settingUpserts.push({ id: name, data: { v: blob[name] }, updated_at: new Date().toISOString() });
        cache.single[name] = json;
      }
    }
    if (settingUpserts.length) {
      jobs.push((async () => bail(await supabase.from("app_settings").upsert(settingUpserts, { onConflict: "id" })))());
    }
    await Promise.all(jobs);
  }

  // One-time: split the legacy kv_store rows into the per-record tables.
  async function migrateFromKv() {
    const { data: rows, error } = await supabase.from("kv_store").select("key,val").like("key", "pooldesk:%");
    if (error) throw error;
    let blob = null;
    for (const row of rows || []) {
      if (row.key === STORE_KEY) {
        try { blob = JSON.parse(row.val); } catch { blob = null; }
      } else {
        const pc = perClientTable(row.key);
        if (pc) {
          let list; try { list = JSON.parse(row.val); } catch { list = null; }
          if (Array.isArray(list)) {
            bail(await supabase.from(pc.table).upsert(
              { id: pc.clientId, client_id: pc.clientId, data: list, updated_at: new Date().toISOString() },
              { onConflict: "id" }));
          }
        }
      }
    }
    if (blob) await saveBlob(blob);
    bail(await supabase.from("app_settings").upsert(
      { id: MIGRATED_FLAG, data: { v: new Date().toISOString() } }, { onConflict: "id" }));
    return blob;
  }

  return {
    async get(key) {
      if (key === STORE_KEY) {
        let { blob, migrated } = await loadBlob();
        const empty = !(blob.clients && blob.clients.length) && blob.schedule === undefined;
        if (empty && !migrated) {
          const migratedBlob = await migrateFromKv();
          if (migratedBlob) return { key, value: JSON.stringify(migratedBlob), shared: true };
          ({ blob } = await loadBlob());
        }
        if (!(blob.clients && blob.clients.length) && blob.schedule === undefined) {
          throw new Error("No such key: " + key); // brand-new install → app seeds
        }
        return { key, value: JSON.stringify(blob), shared: true };
      }
      const pc = perClientTable(key);
      if (pc) {
        const { data, error } = await supabase.from(pc.table).select("data").eq("id", pc.clientId).maybeSingle();
        if (error) throw error;
        if (!data) throw new Error("No such key: " + key);
        return { key, value: JSON.stringify(data.data), shared: true };
      }
      const { data, error } = await supabase.from("kv_store").select("val").eq("key", key).maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("No such key: " + key);
      return { key, value: data.val, shared: true };
    },

    async set(key, value) {
      if (key === STORE_KEY) {
        let blob; try { blob = JSON.parse(String(value)); } catch (e) { throw new Error("Bad data blob: " + e.message); }
        await saveBlob(blob);
        return { key, value, shared: true };
      }
      const pc = perClientTable(key);
      if (pc) {
        let list; try { list = JSON.parse(String(value)); } catch (e) { throw new Error("Bad list: " + e.message); }
        const { error } = await supabase.from(pc.table).upsert(
          { id: pc.clientId, client_id: pc.clientId, data: list, updated_at: new Date().toISOString() },
          { onConflict: "id" });
        if (error) throw error;
        return { key, value, shared: true };
      }
      const { error } = await supabase.from("kv_store")
        .upsert({ key, val: String(value), updated_at: new Date().toISOString() }, { onConflict: "key" });
      if (error) throw error;
      return { key, value, shared: true };
    },

    async delete(key) {
      if (key === STORE_KEY) return { key, deleted: false, shared: true }; // never delete the world
      const pc = perClientTable(key);
      if (pc) {
        const { error } = await supabase.from(pc.table).delete().eq("id", pc.clientId);
        if (error) throw error;
        return { key, deleted: true, shared: true };
      }
      const { error } = await supabase.from("kv_store").delete().eq("key", key);
      if (error) throw error;
      return { key, deleted: true, shared: true };
    },

    // Composed key list so Backup keeps working in shared mode.
    async list(prefix = "") {
      const keys = [STORE_KEY];
      const results = await Promise.all(PER_CLIENT.map(([, table]) => supabase.from(table).select("id")));
      results.forEach(bail);
      PER_CLIENT.forEach(([pfx], i) => {
        for (const row of results[i].data || []) keys.push(pfx + row.id);
      });
      const { data: kvRows, error } = await supabase.from("kv_store").select("key");
      if (!error) for (const r of kvRows || []) if (!keys.includes(r.key)) keys.push(r.key);
      return { keys: keys.filter((k) => k.startsWith(prefix)), prefix, shared: true };
    },
  };
}

export function installStorage() {
  if (typeof window === "undefined") return;
  if (window.storage) return;
  const useSupabase = backendMode() === "supabase" && !!supabase;
  window.storage = useSupabase ? makeSupabase() : makeLocal();
  window.storageBackend = useSupabase ? "supabase" : "local";
}

/* Rough gauge of how full localStorage is: window.storageUsage() */
export function storageUsage() {
  let bytes = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    bytes += k.length + (localStorage.getItem(k) || "").length;
  }
  return { bytes, mb: (bytes / 1048576).toFixed(2), roughLimitMb: 5 };
}
